import { Card, ProgressBar, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Avatar from '@/components/common/Avatar';
import Flex from '@/components/common/Flex';
import { DashboardEmptyNote } from './DashboardEmptyNote';
import { PromptTopBrands } from '../prompts/PromptTopBrands';
import type { ProjectVisibilityPromptPerformance } from '../../types';
import { useTrackerAiPath } from '../../utils/paths';

const AVATAR_COLORS = ['primary', 'success', 'info', 'warning', 'danger'] as const;

const STATUS_LABELS: Record<ProjectVisibilityPromptPerformance['status'], string> = {
  executed: 'اجراشده',
  partial: 'ناقص',
  pending: 'در انتظار',
  failed: 'ناموفق',
  draft: 'پیش‌نویس',
};

function promptInitial(text: string) {
  const trimmed = text.trim();
  return trimmed ? trimmed.charAt(0) : 'پ';
}

export function DashboardRecentPromptsTable({
  prompts,
  positionLabel = 'رتبه میانگین برند ما',
}: {
  prompts: ProjectVisibilityPromptPerformance[];
  positionLabel?: string;
}) {
  const toTrackerAi = useTrackerAiPath();
  return (
    <Card className="shadow-sm">
      <Card.Body className="p-0">
        {prompts.length === 0 ? (
          <DashboardEmptyNote minHeight={160}>در این بازه پرامپتی اجرا نشده.</DashboardEmptyNote>
        ) : (
          <div className="table-responsive scrollbar">
            <Table borderless className="fs--2 mb-0 overflow-hidden">
              <thead className="bg-body-tertiary text-800 text-nowrap align-middle">
                <tr>
                  <th className="text-nowrap">پرامپت</th>
                  <th className="text-center text-nowrap">دیده‌شدن</th>
                  <th className="text-center text-nowrap">امتیاز</th>
                  <th className="text-center text-nowrap">{positionLabel}</th>
                  <th className="text-end text-nowrap">برندها</th>
                </tr>
              </thead>
              <tbody>
                {prompts.map((prompt, index) => {
                  const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
                  return (
                    <tr key={prompt.promptId} className="align-middle white-space-nowrap">
                      <td>
                        <Flex alignItems="center" className="position-relative gap-3">
                          <Avatar
                            size="xl"
                            name={promptInitial(prompt.text)}
                            isExact
                            mediaClass={`text-${color} bg-soft-${color} fs-0`}
                          />
                          <div className="flex-1 min-w-0">
                            <h6 className="mb-0 fw-semi-bold">
                              <Link
                                className="text-1000 stretched-link text-decoration-none"
                                to={toTrackerAi('prompts', `prompt=${prompt.promptId}`)}
                              >
                                <span
                                  className="d-inline-block text-truncate"
                                  style={{ maxWidth: '18rem' }}
                                >
                                  {prompt.text}
                                </span>
                              </Link>
                            </h6>
                            <p className="fs--2 mb-0 text-500">
                              {STATUS_LABELS[prompt.status]}
                              {prompt.brandsFound > 0
                                ? ` · ${prompt.brandsFound} برند`
                                : ''}
                            </p>
                          </div>
                        </Flex>
                      </td>
                      <td className="text-center" style={{ minWidth: '7rem' }}>
                        <ProgressBar
                          now={Math.min(Math.max(prompt.visibilityScore, 0), 100)}
                          style={{ height: 5 }}
                          className="rounded-pill align-middle"
                          variant="primary"
                        />
                      </td>
                      <td
                        className="text-center fw-semi-bold fs--1"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {prompt.visibilityScore}%
                      </td>
                      <td
                        className="text-center fw-semi-bold fs--1"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {prompt.avgPosition != null ? `#${prompt.avgPosition}` : '—'}
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex justify-content-end">
                          <PromptTopBrands
                            status={prompt.status}
                            brands={prompt.topBrands}
                            brandsFound={prompt.brandsFound}
                            max={3}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
